<template>
  <div>
    <centered-spinner v-if="showSpinner" />
    <centered-layout v-else-if="isFlashLoan && isAutogeneratedPassword" :showFooter="true">
      <status :stepStatuses="stepStatuses" @click="handleResetPassword">
        <heading level="2" class="title" slot="title">
          {{ $t('offer.passwordReset.title') }}
        </heading>
        <div class="description" slot="description">
          {{ $t('offer.passwordReset.description', { amount: formatMoney(maxLoanAmount, 0) }) }}
        </div>
        <div class="note" slot="note">
          {{ $t('offer.passwordReset.note') }}
        </div>
        <span slot="buttonLabel">{{ $t('offer.passwordReset.button') }}</span>
      </status>
    </centered-layout>
    <offer-layout v-else class="approvedOffer">
      <heading level="2" class="heading">{{ $t('offer.approved.heading') }}</heading>

      <div class="content">
        <div class="contentSection aboutProduct">
          <div class="productName">{{ $t('offer.productName') }}</div>
          <div class="upTo">{{ $t('offer.upTo') }}</div>
          <div class="maxAmount">{{ formatMoney(maxLoanAmount, 0) }}</div>
          <div class="subHeading expirationNote">
            {{
              $t('offer.approved.expiration', { expirationDateTime: expirationDateTimeFormatted })
            }}
          </div>
        </div>

        <div class="contentSection">
          <div class="title">{{ $t('offer.approved.contentTitle.customize') }}</div>
          <input-inline class="row">
            <div class="label">
              <div>{{ $t('offer.approved.loanAmount.label') }}</div>
              <div class="note">{{ $t('offer.approved.loanAmount.note') }}</div>
            </div>
            <amount-incrementer
              v-model="loanAmount"
              :amounts="loanAmounts"
              :renewalPayoffAmount="renewalPayOffAmount"
              :payoffAmount="splitDisbursementPayOffAmount"
              :isRenewal="isRenewalType"
              :isSplit="isSplitDisbursement"
            />
          </input-inline>

          <input-inline class="row">
            <div class="label">
              <div>{{ $t('offer.approved.loanTerm.label') }}</div>
            </div>
            <input-select
              id="offer-loan-term"
              name="offer-loan-term"
              class="offerLoanTerm"
              v-model="termMonths"
              :defaultValue="$t('select')"
              :options="termOptions"
            />
          </input-inline>
          <template v-if="isSplitDisbursement">
            <input-inline class="row">
              <div class="label adjustLine">
                <div>
                  {{ $t('offer.approved.splitDisbursementPayOffAmountField.label') }}
                  <popover small class="popover" svgName="Icons _ 01 System _ Operations _ Tooltip">
                    {{ $t('offer.approved.splitDisbursementPayOffAmountField.tooltip') }}
                  </popover>
                </div>
              </div>
              <div class="value adjustLine">
                {{ formatMoney(splitDisbursementPayOffAmount, 2) }}
              </div>
            </input-inline>
          </template>
          <input-inline class="row">
            <div class="label">
              <div>{{ $t('offer.approved.originationFeeLabel') }}</div>
            </div>
            <div class="value">
              {{ formatMoney(originationFee, 2) }}
            </div>
          </input-inline>

          <input-inline class="row">
            <div class="label adjustLine">
              <div>
                {{ $t('offer.approved.loanAmountField.label') }}
                <popover small class="popover" svgName="Icons _ 01 System _ Operations _ Tooltip">
                  {{ $t('offer.approved.loanAmountField.tooltip') }}
                </popover>
              </div>
            </div>
            <div class="value adjustLine">
              {{ formatMoney(calcLoanAmount, 2) }}
            </div>
          </input-inline>
          <template v-if="isRenewalType">
            <input-inline class="row">
              <div class="label adjustLine">
                <div>
                  {{ $t('offer.approved.renewalPayoffAmountLabel.label') }}
                  <popover small class="popover" svgName="Icons _ 01 System _ Operations _ Tooltip">
                    {{ $t('offer.approved.renewalPayoffAmountLabel.tooltip') }}
                  </popover>
                </div>
              </div>
              <div class="value adjustLine">
                {{ formatMoney(renewalPayOffAmount, 2) }}
              </div>
            </input-inline>
          </template>
          <template v-if="isSplitDisbursement || isRenewalType">
            <input-inline class="row">
              <div class="label adjustLine">
                <div>
                  {{ $t('offer.approved.disbursementAmountField.label') }}
                  <popover small class="popover" svgName="Icons _ 01 System _ Operations _ Tooltip">
                    {{ $t('offer.approved.disbursementAmountField.tooltip') }}
                  </popover>
                </div>
              </div>
              <div class="value adjustLine">
                {{ formatMoney(disbursementAmount, 2) }}
              </div>
            </input-inline>
            <input-inline class="row">
              <div class="label adjustLine">
                <div>
                  {{ $t('offer.approved.netFundedAmountField.label') }}
                  <popover small class="popover" svgName="Icons _ 01 System _ Operations _ Tooltip">
                    {{ $t('offer.approved.netFundedAmountField.tooltip') }}
                  </popover>
                </div>
              </div>
              <div class="value adjustLine">
                {{ formatMoney(netFundedAmount, 2) }}
              </div>
            </input-inline>
          </template>
          <input-inline class="row">
            <div class="label adjustLine">
              <div>
                {{ $t('offer.approved.totalInterest.label') }}
                <popover small class="popover" svgName="Icons _ 01 System _ Operations _ Tooltip">
                  {{ $t('offer.approved.totalInterest.tooltip') }}
                </popover>
              </div>
            </div>
            <div class="value adjustLine">
              {{ formatMoney(selectedOffer.termLoan.totalInterest, 2) }}
            </div>
          </input-inline>

          <input-inline class="row total">
            <div class="label adjustLine">
              <div>
                {{ $t('offer.approved.totalLoanCost.label') }}
                <popover small class="popover" svgName="Icons _ 01 System _ Operations _ Tooltip">
                  {{ $t('offer.approved.totalLoanCost.tooltip') }}
                </popover>
              </div>
            </div>
            <div class="value adjustLine">
              {{ formatMoney(calcTotalAmount, 2) }}
            </div>
          </input-inline>
        </div>

        <div class="contentSection">
          <div class="title">{{ $t('offer.approved.contentTitle.payment') }}</div>

          <input-inline class="row">
            <div class="label">
              <div>{{ $t('offer.approved.paymentSchedule.label') }}</div>
            </div>
            <div class="value group">
              <radio-button-group
                id="paymentFrequency"
                :options="paymentFrequencyOptions"
                v-model="paymentFrequency"
              />
            </div>
          </input-inline>

          <input-inline class="row total">
            <div class="label">
              <div>{{ periodLabel }}</div>
            </div>
            <div class="value">
              {{ periodPaymentValue }}
            </div>
          </input-inline>
        </div>

        <div class="contentSection">
          <div class="contentSection acceptOffer">
            <brand-button :disabled="disableButtonNext" class="continueButton" @click="onSubmit">
              {{ $t('offer.approved.acceptButtonText') }}
            </brand-button>
          </div>
        </div>
      </div>
    </offer-layout>
  </div>
</template>
<script>
import { compare } from 'fast-json-patch/index.mjs';
import { mapGetters, mapActions } from 'vuex';
import { DateTime } from 'luxon';
import groupBy from 'lodash-es/groupBy';
import reduce from 'lodash-es/reduce';
import deepCopy from 'deep-copy';
import OfferLayout from '@/views/layouts/OfferLayout';
import AmountIncrementer from '@/components/AmountIncrementer';
import InputInline from '@/components/InputInline';
import CenteredSpinner from '@/components/CentredSpinner';
import i18n from '@/locales';
import paymentFrequencies, { WEEKLY } from '@/modules/payment-frequencies';
import { getOfferPaymentInfo, getApplicationOffers } from '@/modules/api-service/v3';
import money from '@/filters/money';
import applicationMixin from '@/mixins/application';
import Status from '@/components/Status';
import CenteredLayout from '@/views/layouts/CenteredLayout';
import getBaseUrl from '@/utils/base-url';
import { wasResetPassword } from '@/modules/get-jwt-claim';
import { COMPLETE } from '@/constants/application-step-statuses';

const TIMEZONE = 'Australia/Brisbane';
const DEFAULT_MAX_DELAY = 60; // 60 sec
const CHECK_DELAY = 5 * 1000; // 5 sec

export default {
  name: 'approved-offer',
  components: {
    OfferLayout,
    AmountIncrementer,
    InputInline,
    CenteredSpinner,
    Status,
    CenteredLayout,
  },
  data() {
    return {
      showSpinner: true,
      stepStatuses: [COMPLETE, COMPLETE, undefined],
      offerOptions: {
        loanAmount: null,
        termMonths: null,
        paymentFrequency: null,
      },
      selectedOffer: { termLoan: {}, paymentDetails: {} },
      detailOffers: {},
      pollStop: false, // stop polling if user navigates away
    };
  },
  async created() {
    this.restoreData();
    if (this.offers.length) {
      this.initializeOfferOptions();
      await this.setSelectedOffer();
    } else {
      const endTime = Date.now() + this.offersPollingWaitTimeout;
      await this.pollForOffers(endTime);
    }
  },
  computed: {
    ...mapGetters('application', ['getApplication']),
    ...mapGetters({
      outstandingBalanceBAIPF: 'offer/getOutstandingBalance',
      offers: 'offer/getOffers',
    }),
    offersPollingWaitTimeout() {
      return (this.$env.VUE_APP_FLASH_LOANS_APPROVED_MAX_DELAY || DEFAULT_MAX_DELAY) * 1000;
    },
    isFlashLoan() {
      return !!this.application._extension.isFlashLoan;
    },
    disableButtonNext() {
      return this.netFundedAmount < 0;
    },
    isAutogeneratedPassword() {
      if (this.application._extension.parentApplicationId) {
        return false;
      }
      return !wasResetPassword(this.$auth.accessToken, this.application);
    },
    maxLoanAmount() {
      return this.defaultSelectedAmount();
    },
    expirationDateTimeFormatted() {
      return DateTime.fromISO(this.selectedOffer.expirationDate)
        .setZone(TIMEZONE)
        .toFormat("LLLL d 'at' ha");
    },
    loanAmount: {
      get() {
        return this.offerOptions.loanAmount;
      },
      set(value) {
        this.offerOptions.loanAmount = value;
        this.setSelectedOffer();
      },
    },
    termMonths: {
      get() {
        const { termMonths } = this.offerOptions;

        return this.availableTerms.includes(termMonths) ? termMonths : this.availableTerms[0];
      },
      set(value) {
        this.offerOptions.termMonths = value;
        this.setSelectedOffer();
      },
    },
    paymentFrequency: {
      get() {
        const { paymentFrequency } = this.offerOptions;

        return this.availableFrequencies.includes(paymentFrequency)
          ? paymentFrequency
          : this.availableFrequencies[0];
      },
      set(value) {
        this.offerOptions.paymentFrequency = value;
        this.setSelectedOffer();
      },
    },
    aggregatedOffers() {
      return this.aggregateOffers();
    },
    loanAmounts() {
      return this.sortedValues('line', '', this.sortDesc);
    },
    months() {
      return this.sortedValues('termLoan', 'termMonths', this.sortDesc);
    },
    availableTerms() {
      const { loanAmount } = this.offerOptions;
      if (this.absentOfferWithAmountAndMonths(loanAmount)) {
        return [];
      }

      return Object.keys(this.aggregatedOffers[loanAmount])
        .map(term => parseInt(term, 10))
        .sort(this.sortDesc);
    },
    availableFrequencies() {
      const { loanAmount } = this.offerOptions;
      const { termMonths } = this;
      if (this.absentOfferWithAmountAndMonths(loanAmount, termMonths)) {
        return [];
      }

      return this.aggregatedOffers[loanAmount][termMonths]
        .map(offer => offer.paymentDetails.paymentFrequency)
        .sort(this.sortPaymentFrequenciesDesc);
    },
    originationFee() {
      return (
        Number(this.selectedOffer.paymentDetails.originationFee || 0) * Number(this.loanAmount || 0)
      );
    },
    paymentFrequencyOptions() {
      return Object.values(paymentFrequencies).map(type => ({
        key: type,
        value: i18n.t(`offer.approved.paymentSchedule.options.${type}`),
        disabled: type.toUpperCase() !== WEEKLY,
      }));
    },
    termOptions() {
      return this.availableTerms.map(option => ({
        key: option,
        value: i18n.t('offer.approved.loanTerm.option', { monthValue: option }),
      }));
    },
    periodLabel() {
      const label = this.paymentFrequency === WEEKLY ? 'weeklyPaymentLabel' : 'dailyPaymentLabel';
      return this.$t(`offer.approved.${label}`);
    },
    periodPaymentValue() {
      return this.formatMoney(this.selectedOffer.termLoan.payment);
    },
    calcLoanAmount() {
      return Number(this.loanAmount || 0) + Number(this.originationFee || 0);
    },
    calcTotalAmount() {
      return this.calcLoanAmount + Number(this.selectedOffer.termLoan.totalInterest || 0);
    },
    isSplitDisbursement() {
      return !!this.splitDisbursementPayOffAmount;
    },
    isRenewalType() {
      return !!this.selectedOffer.productId.includes('RENEWALS');
    },
    disbursementAmount() {
      return Number(this.loanAmount || 0);
    },
    splitDisbursementPayOffAmount() {
      return Number(this.selectedOffer?.splitDisbursementPayOffAmount || 0);
    },
    netFundedAmount() {
      return (
        this.disbursementAmount - this.renewalPayOffAmount - this.splitDisbursementPayOffAmount
      );
    },
    renewalPayOffAmount() {
      return Number(this.outstandingBalanceBAIPF || 0);
    },
  },
  methods: {
    async handleResetPassword() {
      const baseUrl = getBaseUrl();
      baseUrl.hash = '';
      const email = encodeURIComponent(this.application.owners[0].email);
      await this.$auth.logOut(`${baseUrl.href}?username=${email}&reset_context=true#login`);
    },
    generatePatch() {
      return compare(this.getApplication(), this.application);
    },
    formatMoney: money,
    absentOfferWithAmountAndMonths(amount, months) {
      return months
        ? !this.aggregatedOffers[amount] || !this.aggregatedOffers[amount][months]
        : !this.aggregatedOffers[amount];
    },
    async restoreData() {
      this.application = await this.getApplication();
    },
    async pollForOffers(endTime) {
      while (!this.pollStop && Date.now() < endTime) {
        const { data } = await getApplicationOffers(this.application.id);

        this.$store.dispatch('offer/deleteAll', null, { root: true });
        this.$store.dispatch('offer/createOffers', data, { root: true });
        if (this.offers.length) {
          this.pollStop = true;
          return;
        }

        await new Promise(resolve => setTimeout(resolve, CHECK_DELAY));
      }

      if (!this.pollStop) {
        // timeout reached without offers
        this.$router.replace({ name: 'unableProcess' });
      }
    },
    initializeOfferOptions() {
      this.offerOptions.loanAmount = this.defaultSelectedAmount();
      [this.offerOptions.termMonths] = this.availableTerms;
      [this.offerOptions.paymentFrequency] = this.availableFrequencies;
    },
    aggregateOffers() {
      const groupedByAmount = groupBy(this.offers, offer => offer.line);

      return reduce(
        groupedByAmount,
        (result, amounts, amount) => {
          const groupedByTerms = groupBy(amounts, offer => offer.termLoan.termMonths);

          return { ...result, [amount]: groupedByTerms };
        },
        {},
      );
    },
    sortedValues(namespace, key, sortFn) {
      const collection = this.offers.reduce((res, offer) => {
        const offerRes = key ? offer[namespace][key] : offer[namespace];

        return res.add(offerRes);
      }, new Set());

      return [...collection].sort(sortFn);
    },
    sortDesc(valueA, valueB) {
      return valueB - valueA;
    },
    sortPaymentFrequenciesDesc(a, b) {
      if (a > b) {
        return -1;
      }
      if (a < b) {
        return 1;
      }
      return 0;
    },
    defaultSelectedAmount() {
      return Math.max(...this.loanAmounts);
    },
    async setSelectedOffer() {
      const { loanAmount } = this.offerOptions;
      if (this.absentOfferWithAmountAndMonths(loanAmount)) return;

      const { termMonths, paymentFrequency } = this;
      const findOffer = this.aggregatedOffers[loanAmount][termMonths].find(
        offer => offer.paymentDetails.paymentFrequency === paymentFrequency,
      );

      this.selectedOffer = deepCopy(findOffer);
      const detailOffer = await this.getDetailOffer(this.selectedOffer.id);

      this.selectedOffer.termLoan.payment = detailOffer.paymentAmount;
      this.selectedOffer.termLoan.totalLoanCost = detailOffer.totalLoanCost;
      this.selectedOffer.termLoan.totalInterest = detailOffer.totalInterest;
    },
    async getDetailOffer(offerId) {
      if (!this.detailOffers[this.selectedOffer.id]) {
        if (!this.showSpinner) this.$overlay.show();

        const { data: detailOfferRaw } = await getOfferPaymentInfo(this.application.id, offerId);
        this.detailOffers[offerId] = {
          totalInterest: detailOfferRaw.total.interestExpected,
          totalLoanCost: detailOfferRaw.total.totalDue,
          paymentAmount: detailOfferRaw.payments[0].totalDue,
        };

        this.$overlay.hide();
        this.showSpinner = false;
      }
      return this.detailOffers[this.selectedOffer.id];
    },
    async onSubmit() {
      this.$overlay.show();
      await this.submitApprovedOffer({
        offerIds: [this.selectedOffer.id],
        acceptedLoanAmount: this.selectedOffer.line,
        applicationId: this.application.id,
        ...(this.isRenewalType && {
          split: {
            disbursementAmount: this.netFundedAmount,
            payOffAmount: this.outstandingBalanceBAIPF,
          },
        }),
      });
      this.$overlay.hide();
    },
  },
  watch: {
    async offers(newOffers) {
      if (newOffers.length) {
        this.initializeOfferOptions();
        await this.setSelectedOffer();
      }
    },
  },
  beforeDestroy() {
    this.pollStop = true;
  },
  mixins: [applicationMixin],
};
</script>
<style lang="sass" scoped>
$contentBreakpoint: $tyranno

.heading
  text-align: center
  margin-top: baseunit(6.8)

.adjustLine
  margin-top: baseunit(1) !important

.expirationNote
  +font-size(13.33, 20)
  color: $font-color-light
  margin-top: $glass-spacing-05

.approvedOffer
  color: $font-color-dark

  .content
    background: $color-system-neutral-white
    margin: $glass-spacing-4 auto $glass-spacing-6
    padding: $glass-spacing-3
    border-radius: 4px
    border: 1px solid $color-system-neutral-gray-3
    +mq($contentBreakpoint)
      max-width: baseunit(68)

    .contentSection
      .title
        +font-size(16, 28)
        font-weight: bold
        padding-bottom: $glass-spacing-3
        color: $glass-color-brand-primary-dark-03
      .row
        margin: $glass-spacing-105 0
        +font-size(16, 28)
        .label
          display: flex
          flex-direction: column
          justify-content: center
      .total
        font-weight: bold
      &:not(:first-of-type)
        padding-top: $glass-spacing-3
        border-top: 1px solid $color-system-neutral-gray-3
      &:not(:last-of-type)
        padding-bottom: $glass-spacing-3

.aboutProduct
  text-align: center

  .productName
    +font-size(16, 28)
    font-weight: bold
  .upTo
    +font-size(13.33, 20)
    color: $color-system-neutral-gray-5
  .maxAmount
    +font-size(32, 32)
    margin-top: $glass-spacing-05
    color: $glass-color-brand-primary-dark-03
    font-family: $heading-partner-font-family
    font-weight: bold
  .productDescription
    +font-size(13.33, 20)
    color: $color-system-neutral-gray-5
    margin-top: $glass-spacing-105

.note
  +font-size(13.33, 18)

.acceptOffer
  text-align: center
  +mq($contentBreakpoint)
    padding: 0 baseunit(10.4)

  .productName
    --font-family: inherit
    ::v-deep .sup
      vertical-align: text-top
      top: -$glass-spacing-025
      +font-size(16)

  .description
    +font-size(13.33, 16)
    color: $color-system-neutral-gray-5
    margin-top: $glass-spacing-1
    white-space: pre-line

.continueButton
  margin-top: $glass-spacing-105

.group
  display: inline-flex
  ::v-deep
    label
      width: baseunit(10)

.popover
  vertical-align: super
  font-weight: normal
  ::v-deep
    .icon
      color: $glass-color-brand-primary-dark-03
    .content
      background: $glass-color-brand-secondary-accent-01
      color: #000000
      z-index: 2
    .iconButton
      &:focus
        outline: 0
        box-shadow: 0 0 0 4px $glass-color-brand-primary-light-02
</style>
